// Libraries
import { Grid, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useState } from "react";

// Components
import VehicleSectionAmount from "./VehicleSectionAmount";

// Types
import { IAdministrationTools, ITypeDefaultTradeAmount } from "@/Settings/types/administrationTools/model";
import { TOnSaveAmount } from "@/Settings/types/administrationTools/dealerTradeAmountValue";

// Mutation
import { updateAdministrationTools } from "@/Settings/api/AdministrationTools";
import { and } from "@/Common/helpers/sonar";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Constants
const referenceOptionsNewVehicle = ["Invoice Price", "Internet Price", "MSRP"];
const referenceOptionsPreOwned = ["Cost (Sales Cost)", "Internet Price", "Accounting cost (Inventory GL Amount)"];

type TDealerTradeAmountValuesProps = {
	settings: IAdministrationTools;
};

export default function DealerTradeAmountValues(props: Readonly<TDealerTradeAmountValuesProps>) {
	const { settings } = props;

	const queryClient = useQueryClient();

	const locationId = useSelector((state: RootState) => state.location.location?.locationId);

	const [typeUpdate, setTypeUpdate] = useState<"newVehicle" | "preOwnedVehicle" | null>(null);

	function initDefaultValues(type: "newVehicle" | "preOwnedVehicle") {
		if (settings?.integrations?.defaultTradeAmount?.[`${type}`]) {
			return settings.integrations.defaultTradeAmount[`${type}`];
		}

		return {
			reference: "",
			modifier: { value: null, type: "Dollar" },
		} as ITypeDefaultTradeAmount;
	}

	const { mutate: mutateDefaultValues, isPending: isLoading } = useMutation({
		mutationKey: ["administrationToolsDefaultValuesMutation", locationId],
		mutationFn: (input: Partial<IAdministrationTools>): Promise<Partial<IAdministrationTools>> => {
			if (locationId) {
				return updateAdministrationTools(locationId, input) as Promise<Partial<IAdministrationTools>>;
			}
			return Promise.resolve({ integrations: { defaultTradeAmount: {} } } as Partial<IAdministrationTools>);
		},
		onError: (error) => {
			errorLogger(error.message);
		},
		onSuccess: (data: Partial<IAdministrationTools>) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return {
					...cachedData,
					integrations: {
						...cachedData?.integrations,
						defaultTradeAmount: data.integrations?.defaultTradeAmount,
					},
				};
			});
		},
	});

	function onSave(params: TOnSaveAmount) {
		const { type, obj } = params;

		const referenceChanges = Boolean(obj?.reference);
		const modifierChanges = obj.modifier.value !== null;

		if (and(referenceChanges, modifierChanges)) {
			setTypeUpdate(type);
			mutateDefaultValues({
				integrations: {
					...settings.integrations,
					defaultTradeAmount: { ...settings.integrations.defaultTradeAmount, [`${type}`]: obj },
				},
			});
		}
	}

	return (
		<Grid item container direction={"row"} marginTop={2} className="!my-[32px]" xs={12}>
			<Grid item xs={12} mb={3}>
				<Typography className="!font-montserrat !text-default !text-[20px] !font-medium">
					Dealer Trade Default Transfer Amounts
				</Typography>
			</Grid>
			<Grid item xs={4}>
				<VehicleSectionAmount
					key={"newVehicle"}
					typeVehicle="newVehicle"
					title={"New Vehicles"}
					referenceOptions={referenceOptionsNewVehicle}
					value={initDefaultValues("newVehicle")}
					onSave={onSave}
					isLoading={isLoading && typeUpdate === "newVehicle"}
				/>
			</Grid>
			<Grid item xs={4}>
				<VehicleSectionAmount
					key={"preOwnedVehicle"}
					typeVehicle="preOwnedVehicle"
					title={"Pre-Owned Vehicles"}
					referenceOptions={referenceOptionsPreOwned}
					value={initDefaultValues("preOwnedVehicle")}
					onSave={onSave}
					isLoading={isLoading && typeUpdate === "preOwnedVehicle"}
				/>
			</Grid>
		</Grid>
	);
}
